import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, MapPin, X } from "lucide-react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { useSeo } from "../lib/useSeo";
import { ListingCard, ListingCardSkeleton } from "../components/ListingCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { HuurKoopSwitch } from "../components/koop/HuurKoopSwitch";
import { PropertySearchBar } from "../components/search/PropertySearchBar";

const ENERGY = ["A", "B", "C", "D", "E", "F", "G"];
const emptyFilters = { city: "", min_price: 0, max_price: 3000, property_type: "", bedrooms: 0, min_m2: 0, furnished: false, pets: false, energy_label: "" };

const parseFilters = (sp) => ({
  city: sp.get("city") || "",
  min_price: sp.get("min_price") ? Number(sp.get("min_price")) : 0,
  max_price: sp.get("max_price") ? Number(sp.get("max_price")) : 3000,
  property_type: sp.get("property_type") || "",
  bedrooms: sp.get("bedrooms") ? Number(sp.get("bedrooms")) : 0,
  min_m2: sp.get("min_m2") ? Number(sp.get("min_m2")) : 0,
  furnished: sp.get("furnished") === "true",
  pets: sp.get("pets") === "true",
  energy_label: sp.get("energy_label") || "",
});

const buildParams = (f, s) => {
  const p = {};
  if (f.city) p.city = f.city;
  if (f.min_price) p.min_price = String(f.min_price);
  if (f.max_price && f.max_price < 5000) p.max_price = String(f.max_price);
  if (f.property_type) p.property_type = f.property_type;
  if (f.bedrooms) p.bedrooms = String(f.bedrooms);
  if (f.min_m2) p.min_m2 = String(f.min_m2);
  if (f.furnished) p.furnished = "true";
  if (f.pets) p.pets = "true";
  if (f.energy_label) p.energy_label = f.energy_label;
  if (s && s !== "newest") p.sort = s;
  return p;
};

const FiltersForm = ({ filters, setFilters, onApply, onReset }) => {
  const { t } = useT();
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-6">
      <div>
        <Label className="text-sm font-semibold text-navy">{t("woningen.f.city")}</Label>
        <Input
          data-testid="filter-city"
          value={filters.city}
          onChange={(e) => set("city", e.target.value)}
          placeholder="Amsterdam"
          className="mt-2 rounded-xl"
        />
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-navy">{t("woningen.f.price")}</Label>
          <span className="text-sm font-bold text-brandblue">
            €{filters.min_price} – €{filters.max_price}
          </span>
        </div>
        <Slider
          data-testid="filter-minprice"
          className="mt-3"
          min={0}
          max={5000}
          step={50}
          value={[filters.min_price, filters.max_price]}
          onValueChange={(v) => {
            set("min_price", v[0]);
            set("max_price", v[1]);
          }}
        />
      </div>
      <div>
        <Label className="text-sm font-semibold text-navy">{t("woningen.f.bedrooms")}</Label>
        <div className="mt-2 flex gap-2">
          {[0, 1, 2, 3, 4].map((n) => (
            <button
              key={n}
              data-testid={`filter-bedrooms-${n}`}
              onClick={() => set("bedrooms", n)}
              className={`h-10 flex-1 rounded-xl border text-sm font-semibold transition-colors ${
                filters.bedrooms === n ? "border-brandblue bg-brandblue-light text-brandblue" : "border-border text-slate-600"
              }`}
            >
              {n === 0 ? "Alle" : `${n}+`}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-navy">{t("woningen.f.minm2")}</Label>
          <span className="text-sm font-bold text-brandblue">{filters.min_m2} m²</span>
        </div>
        <Slider
          className="mt-3"
          min={0}
          max={150}
          step={5}
          value={[filters.min_m2]}
          onValueChange={(v) => set("min_m2", v[0])}
        />
      </div>
      <div>
        <Label className="text-sm font-semibold text-navy">{t("woningen.f.energy")}</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {ENERGY.map((e) => (
            <button
              key={e}
              onClick={() => set("energy_label", filters.energy_label === e ? "" : e)}
              className={`h-9 w-9 rounded-lg border text-sm font-bold transition-colors ${
                filters.energy_label === e ? "border-brandblue bg-brandblue-light text-brandblue" : "border-border text-slate-600"
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
        <Label className="text-sm font-medium text-navy">{t("woningen.f.furnished")}</Label>
        <Switch data-testid="filter-furnished" checked={filters.furnished} onCheckedChange={(v) => set("furnished", v)} />
      </div>
      <div className="flex items-center justify-between rounded-xl border border-border px-4 py-3">
        <Label className="text-sm font-medium text-navy">{t("woningen.f.pets")}</Label>
        <Switch data-testid="filter-pets" checked={filters.pets} onCheckedChange={(v) => set("pets", v)} />
      </div>
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onReset} className="flex-1 rounded-xl" data-testid="filter-reset">
          {t("woningen.reset")}
        </Button>
        <Button onClick={onApply} className="flex-1 rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="filter-apply">
          {t("woningen.apply")}
        </Button>
      </div>
    </div>
  );
};

const Woningen = () => {
  const { t } = useT();
  useSeo({ title: t("seo.woningen.title"), description: t("seo.woningen.desc"), image: "/og-image.png" });
  // Filters live in the URL so they survive detail navigation, back/forward,
  // sharing and bookmarking. `applied` is derived from the URL; `filters` is the
  // editable draft in the form.
  const [searchParams, setSearchParams] = useSearchParams();
  const spString = searchParams.toString();
  const applied = parseFilters(searchParams);
  const sort = searchParams.get("sort") || "newest";
  const [filters, setFilters] = useState(applied);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE = 48;

  const buildQuery = (f, s, offset) => {
    const params = new URLSearchParams();
    if (f.city) params.set("city", f.city);
    if (f.min_price) params.set("min_price", f.min_price);
    if (f.max_price && f.max_price < 5000) params.set("max_price", f.max_price);
    if (f.property_type) params.set("property_type", f.property_type);
    if (f.bedrooms) params.set("bedrooms", f.bedrooms);
    if (f.min_m2) params.set("min_m2", f.min_m2);
    if (f.furnished) params.set("furnished", "true");
    if (f.pets) params.set("pets", "true");
    if (f.energy_label) params.set("energy_label", f.energy_label);
    params.set("sort", s);
    params.set("limit", PAGE);
    params.set("offset", offset);
    return params.toString();
  };

  const fetchListings = useCallback((f, s) => {
    setData(null);
    setError(false);
    api(`/listings?${buildQuery(f, s, 0)}`)
      .then((d) => setData(d))
      .catch(() => setError(true));
  }, []);

  const loadMore = () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    api(`/listings?${buildQuery(applied, sort, data.listings.length)}`)
      .then((d) =>
        setData((prev) => ({ ...d, listings: [...(prev?.listings || []), ...d.listings] }))
      )
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  // Refetch + re-sync the draft whenever the URL filters change (apply, pills,
  // sort, back/forward, external links).
  useEffect(() => {
    const sp = new URLSearchParams(spString);
    setFilters(parseFilters(sp));
    fetchListings(parseFilters(sp), sp.get("sort") || "newest");
  }, [spString, fetchListings]);

  const commit = (f, s = sort) => setSearchParams(buildParams(f, s));

  const apply = () => {
    commit(filters);
    setSheetOpen(false);
  };
  const reset = () => {
    setFilters({ ...emptyFilters });
    setSearchParams({});
    setSheetOpen(false);
  };

  const listings = data?.listings || [];

  const QUICK_CITIES = ["Amsterdam", "Rotterdam", "Utrecht", "Den Haag", "Eindhoven", "Groningen"];
  const applyCity = (city) => {
    commit({ ...emptyFilters, city });
  };
  const activePills = [];
  if (applied.city) activePills.push({ k: "city", label: applied.city });
  if (applied.min_price) activePills.push({ k: "min_price", label: `≥ €${applied.min_price}` });
  if (applied.max_price && applied.max_price < 5000) activePills.push({ k: "max_price", label: `≤ €${applied.max_price}` });
  if (applied.property_type) activePills.push({ k: "property_type", label: applied.property_type.charAt(0).toUpperCase() + applied.property_type.slice(1) });
  if (applied.bedrooms) activePills.push({ k: "bedrooms", label: `${applied.bedrooms}+ ${t("common.beds")}` });
  if (applied.min_m2) activePills.push({ k: "min_m2", label: `≥ ${applied.min_m2} m²` });
  if (applied.energy_label) activePills.push({ k: "energy_label", label: `Label ${applied.energy_label}` });
  if (applied.furnished) activePills.push({ k: "furnished", label: t("woningen.f.furnished") });
  if (applied.pets) activePills.push({ k: "pets", label: t("woningen.f.pets") });
  const removePill = (key) => {
    commit({ ...applied, [key]: emptyFilters[key] });
  };

  return (
    <div>
      {/* Hero header with photo backdrop + uniform navy overlay (matches homepage) */}
      <div className="relative overflow-hidden bg-navy">
        <img
          src="https://customer-assets-jt897jd0.emergentagent.net/job_ad0cee43-adb9-42e2-b070-e52d3c5da298/artifacts/gi8kydv6_Monochrome%20villa%20with%20infinitypool.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-navy/85" />
        <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-brandblue/30 blur-3xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6">
          <HuurKoopSwitch active="huur" className="mb-6" />
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-3xl font-extrabold text-white sm:text-4xl">{t("woningen.title")}</h1>
              <p className="mt-1 flex items-center gap-2 text-sm text-white/70" data-testid="listings-count">
                {data ? (
                  <>
                    <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-success" />
                    {t("woningen.count", { n: data.total })}
                  </>
                ) : (
                  t("common.loading")
                )}
              </p>
            </div>
          </div>
          {/* Compact search bar (Funda-style) */}
          <div className="mt-6">
            <PropertySearchBar
              variant="compact"
              defaultMode="huur"
              initial={{
                city: applied.city,
                min_price: applied.min_price || undefined,
                max_price: applied.max_price < 5000 ? applied.max_price : undefined,
                property_type: applied.property_type,
              }}
            />
          </div>
          {/* quick city chips */}
          <div className="no-scrollbar mt-5 flex gap-2 overflow-x-auto pb-1">
            {QUICK_CITIES.map((c) => (
              <button
                key={c}
                data-testid={`quick-city-${c}`}
                onClick={() => applyCity(c)}
                className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                  applied.city === c ? "bg-white text-navy" : "bg-white/10 text-white/80 hover:bg-white/20"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {activePills.length === 0 ? (
              <span className="text-sm text-slate-400">{t("woningen.noFilters")}</span>
            ) : (
              activePills.map((p) => (
                <button
                  key={p.k}
                  onClick={() => removePill(p.k)}
                  data-testid={`pill-${p.k}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brandblue-light px-3 py-1.5 text-sm font-semibold text-brandblue"
                >
                  {p.label} <X size={13} />
                </button>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
          <Select value={sort} onValueChange={(s) => commit(applied, s)}>
            <SelectTrigger className="w-[170px] rounded-xl" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t("woningen.sort.newest")}</SelectItem>
              <SelectItem value="price_asc">{t("woningen.sort.price_asc")}</SelectItem>
              <SelectItem value="match_desc">{t("woningen.sort.match_desc")}</SelectItem>
              <SelectItem value="size_desc">{t("woningen.sort.size_desc")}</SelectItem>
            </SelectContent>
          </Select>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="outline" className="rounded-xl" data-testid="open-filters">
                <SlidersHorizontal size={16} className="mr-2" /> {t("woningen.filters")}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[340px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-display">{t("woningen.filters")}</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FiltersForm filters={filters} setFilters={setFilters} onApply={apply} onReset={reset} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-3xl border border-border bg-white p-6 shadow-sm">
            <h2 className="mb-5 flex items-center gap-2 font-display text-lg font-bold text-navy">
              <SlidersHorizontal size={18} /> {t("woningen.filters")}
            </h2>
            <FiltersForm filters={filters} setFilters={setFilters} onApply={apply} onReset={reset} />
          </div>
        </aside>

        <section>
          {error ? (
            <div className="rounded-3xl border border-border p-12 text-center">
              <p className="text-slate-500">{t("common.error")}</p>
              <Button onClick={() => fetchListings(applied, sort)} className="mt-4 rounded-xl">
                {t("common.retry")}
              </Button>
            </div>
          ) : data === null ? (
            <div className="grid gap-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <ListingCardSkeleton key={i} />
              ))}
            </div>
          ) : listings.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border p-16 text-center" data-testid="listings-empty">
              <MapPin className="mx-auto text-slate-300" size={44} />
              <p className="mt-4 text-slate-500">{t("woningen.empty")}</p>
            </div>
          ) : (
            <div className="grid gap-5">
              {listings.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          )}
          {data?.has_more && listings.length > 0 && (
            <div className="mt-10 flex justify-center">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                className="rounded-xl px-8"
                data-testid="load-more-btn"
              >
                {loadingMore ? t("common.loading") : `${t("woningen.loadMore")} (${listings.length}/${data.total})`}
              </Button>
            </div>
          )}
        </section>
      </div>
      </div>
    </div>
  );
};

export default Woningen;
